import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNotificationsContext } from '@hooks/context/useNotificationsContext';
import { useLocales } from '@hooks/helpers/useLocales';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiNotificationService, apiTransactionsService } from '@api';
import { ENotificationCategory, TNotification } from '@xeppt/xeppt-sdk/types/notification';
import { formatDate, groupNotificationsByMonth } from '@utils/index';
import Skeleton from '@components/common/skeleton';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import { Icon, TIconType } from '@components/icons';
import Button from '@components/common/button';
import Checkbox from '@components/common/checkbox';
import classNames from 'classnames/bind';
import { routes } from '@const/routes';
import { executeNotificationsData } from '@utils/notifications';
import emptyIllustration from '@svg/illustrations/empty_notifications.svg';
import { useHelpCenterModals } from '@hooks/modals/useHelpCenterModals';
import useModalContext from '@hooks/context/useModalContext';
import { modalIds } from '@const/modals';
import { EUserStatus, TTransaction } from '@xeppt/xeppt-sdk/types';
import { useApiQuery } from '@hooks/api/useApiQuery';

interface IProps {
    isOpen: boolean;
    onClose: () => void;
}

const cx = classNames.bind(styles);

const NotificationsSidebar: FC<IProps> = ({ isOpen, onClose }) => {
    const { t } = useTranslation('notifications');
    // const { t: titleLocale } = useTranslation('settings', {
    //     keyPrefix: 'notifications.tabs'
    // });
    const { t: categoryLocalization } = useTranslation('settings', {
        keyPrefix: 'notifications.tabs'
    });
    const { changeModalData, onOpen } = useModalContext();
    const navigate = useNavigate();
    const {
        notifications,
        isNotificationsLoading,
        refetchNotifications,
        totalNotifications,
        loadMoreNotifications,
        handleGetVerificationResult
    } = useNotificationsContext();
    const { handleOpenSubmitRequestModal } = useHelpCenterModals();
    const { requestErrorLocale, requestSuccessLocale, submitLocale } = useLocales();
    const [selected, setSelected] = useState<string[]>([]);
    const [readNotifications, setReadNotifications] = useState<string[]>([]);
    const [chosenNotification, setChosenNotification] = useState<TNotification>();

    useEffect(() => {
        if (!isOpen) {
            setSelected([]);
            setChosenNotification(undefined);
        }
    }, [isOpen]);

    const { handleRequest: handleReadNotifications } = useApiMutation({
        method: (ids: string[]) => apiNotificationService.readNotification(ids),
        onSuccess: () => {
            refetchNotifications();
            setSelected([]);
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleBulkDeleteNotifications } = useApiMutation({
        method: (id?: string) => {
            return apiNotificationService.bulkDeleteNotifications(id ? [id] : selected);
        },
        onSuccess: () => {
            refetchNotifications();
            setChosenNotification(undefined);
            setSelected([]);
            requestSuccessLocale('delete_notifications');
        },
        onError: requestErrorLocale
    });

    const onReadNotification = (notification: TNotification) => {
        if (!notification.is_read) {
            setReadNotifications((state) => [...state, notification.id]);
            handleReadNotifications([notification.id]);
        }
    };

    const handleChangeSelected = (id: string) => {
        if (selected.includes(id)) {
            setSelected((state) => state.filter((item) => item !== id));
        } else {
            setSelected((state) => [...state, id]);
        }
    };

    const wrapperStyle = useMemo(() => cx([styles.wrapper, { isOpen }]), [isOpen]);

    const notificationDetailsData = useMemo(() => {
        if (!chosenNotification)
            return {
                category: ENotificationCategory.CARD,
                icon: '',
                title: '',
                description: '',
                actions: {
                    isSupport: false,
                    isRequest: false,
                    isViewLimits: false,
                    isTransaction: false,
                    actions: []
                }
            };
        return executeNotificationsData({
            notification: chosenNotification,
            navigate,
            handleGetVerificationResult: (codes, status) => {
                handleGetVerificationResult(
                    codes,
                    codes ? EUserStatus.UNVERIFIED : EUserStatus.ACTIVE,
                    true
                );
                onClose();
                setSelected([]);
                setChosenNotification(undefined);
            },
            dateLocale: t
        });
    }, [chosenNotification]);

    const handleSelectAll = () => {
        if (selected.length === notifications.length) {
            setSelected([]);
        } else {
            setSelected(notifications.map((notification) => notification.id));
        }
    };

    const getCategoryColor = (category?: ENotificationCategory) => {
        switch (category) {
            case ENotificationCategory.CARD:
                return '#0C91DA';
            case ENotificationCategory.MARKETING:
                return '#0C884C';
            case ENotificationCategory.PAYMENTS:
                return '#DB9E2F';
            case ENotificationCategory.PERSONAL:
                return '#4B2F94';
            case ENotificationCategory.SCHEDULE:
                return '#C61350';
            case ENotificationCategory.LOYALTY:
                return 'rgba(0, 52, 209, 1)';
            default:
                return '#DB9E2F';
        }
    };

    const handleViewTransaction = (transaction: TTransaction) => {
        changeModalData({ transaction });
        onOpen({
            modalId: modalIds.VIEW_TRANSACTION
        });
    };

    const { handleRequest: fetchTransaction } = useApiQuery({
        method: () =>
            apiTransactionsService.getTransactionById(
                //@ts-ignore
                chosenNotification?.payload?.transaction_id || ''
            ),
        isInitialRequest: false,
        condition: !!chosenNotification,
        onSuccess: (data) => {
            handleViewTransaction(data);
        }
    });

    return (
        <>
            <div className={cx(styles.overlay, { isOpen })} onClick={onClose} />
            <div className={wrapperStyle}>
                <div className={cx([styles.details, { isOpen: !!chosenNotification }])}>
                    <div className={styles.actions_header}>
                        <button onClick={() => setChosenNotification(undefined)}>
                            <Icon name="nav_right" />
                        </button>
                        <Button
                            size="custom"
                            leftIcon="trash"
                            onClick={() => {
                                handleBulkDeleteNotifications(chosenNotification?.id);
                            }}
                        />
                    </div>
                    {chosenNotification && (
                        <div className={styles.header}>
                            <div className={styles.title_wrapper}>
                                <div className={styles.header_icon}>
                                    <div className={styles.icon}>
                                        <Icon
                                            style={{
                                                color: getCategoryColor(
                                                    notificationDetailsData?.category
                                                )
                                            }}
                                            name={notificationDetailsData?.icon as TIconType}
                                        />
                                    </div>
                                    <Typography className={styles.details_title}>
                                        {categoryLocalization(
                                            notificationDetailsData?.category || 'card'
                                        )}
                                    </Typography>
                                </div>
                                <span className={styles.date}>
                                    {formatDate(
                                        chosenNotification?.created_at,
                                        'DD MMM YYYY, HH:mm'
                                    )}
                                </span>
                            </div>
                            <Typography
                                variant="h3"
                                fw={700}
                                className={styles.details_main_title}
                                dangerouslySetInnerHTML={{
                                    __html: chosenNotification?.title || ''
                                }}
                            />
                            <div className={styles.notification_content}>
                                <div className={styles.body}>
                                    <Typography
                                        variant="body1"
                                        dangerouslySetInnerHTML={{
                                            __html: chosenNotification?.body || ''
                                        }}
                                    />
                                </div>
                                <div className={styles.details_actions}>
                                    {notificationDetailsData?.actions?.actions?.map((item, i) => {
                                        return (
                                            <Button
                                                key={i}
                                                onClick={item.onClick}
                                                variant={item.variant}
                                                size="medium">
                                                <span
                                                    dangerouslySetInnerHTML={{ __html: item.label }}
                                                />
                                            </Button>
                                        );
                                    })}
                                    {notificationDetailsData?.actions?.isSupport && (
                                        <Button
                                            onClick={() => {
                                                onClose();
                                                handleOpenSubmitRequestModal();
                                            }}
                                            variant="primary"
                                            size="medium"
                                            className={styles.details_button}>
                                            {submitLocale('contact_support')}
                                        </Button>
                                    )}
                                    {notificationDetailsData?.actions?.isTransaction && (
                                        <Button
                                            onClick={() => {
                                                onClose();
                                                fetchTransaction();
                                            }}
                                            variant="primary"
                                            size="medium"
                                            className={styles.details_button}>
                                            {submitLocale('view_transaction_details')}
                                        </Button>
                                    )}
                                    {notificationDetailsData?.actions?.isViewLimits && (
                                        <Button
                                            onClick={() => navigate(routes.limits)}
                                            variant="primary"
                                            size="medium"
                                            className={styles.details_button}>
                                            {submitLocale('view_limits')}
                                        </Button>
                                    )}
                                    {notificationDetailsData?.actions?.isRequest && (
                                        <Button
                                            onClick={() => {
                                                onClose();
                                                fetchTransaction();
                                            }}
                                            variant="primary"
                                            size="medium"
                                            className={styles.details_button}>
                                            {submitLocale('view_request_details')}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className={cx([styles.content, { isDetailsOpen: !!chosenNotification }])}>
                    <button className={styles.close_button} onClick={onClose}>
                        <Icon name="close" />
                    </button>
                    <div className={styles.header}>
                        <Typography variant="h4">Notifications</Typography>
                    </div>
                    <div className={styles.actions_wrapper}>
                        <div className={styles.choose_all}>
                            {notifications.length > 0 && (
                                <>
                                    <Checkbox
                                        isDelete
                                        onClick={handleSelectAll}
                                        checked={
                                            selected.length === notifications.length &&
                                            notifications.length !== 0
                                        }
                                    />{' '}
                                    {selected.length} chosen
                                </>
                            )}
                        </div>
                        <div className={styles.actions}>
                            {selected.length !== 0 && (
                                <Button
                                    size="custom"
                                    leftIcon="trash"
                                    onClick={() =>
                                        selected.length > 0 &&
                                        handleBulkDeleteNotifications(undefined)
                                    }
                                />
                            )}
                            {selected.length !== 0 && (
                                <Button
                                    size="custom"
                                    leftIcon="double_check"
                                    onClick={() =>
                                        selected.length > 0 && handleReadNotifications(selected)
                                    }
                                />
                            )}
                            <Button
                                size="custom"
                                leftIcon="settings"
                                onClick={() => navigate(routes.settings_notifications)}
                            />
                            <Button
                                size="custom"
                                leftIcon="refresh"
                                onClick={refetchNotifications}
                            />
                        </div>
                    </div>
                    {!isNotificationsLoading && notifications.length === 0 && (
                        <div className={styles.empty_wrapper}>
                            <img src={emptyIllustration} alt="" />
                            <p>You don't have any notifications for this time</p>
                        </div>
                    )}
                    <div className={styles.notifications_wrapper}>
                        {!isNotificationsLoading && notifications.length !== 0 && (
                            <div className={styles.notification_block}>
                                {groupNotificationsByMonth(notifications || [])?.map((item) => {
                                    return (
                                        <div
                                            key={item.month}
                                            className={styles.notification_month_block}>
                                            <div className={styles.month}>{item.month}</div>
                                            {item.notifications?.map((item) => {
                                                const { category, date, icon } =
                                                    executeNotificationsData({
                                                        notification: item,
                                                        navigate,
                                                        handleGetVerificationResult,
                                                        dateLocale: t
                                                    });
                                                return (
                                                    <div
                                                        className={styles.notification_wrapper}
                                                        key={item.id}>
                                                        <Checkbox
                                                            checked={selected.includes(item.id)}
                                                            onClick={() => {
                                                                handleChangeSelected(item.id);
                                                            }}
                                                        />
                                                        <div className={styles.icon}>
                                                            <Icon
                                                                style={{
                                                                    color: getCategoryColor(
                                                                        category
                                                                    )
                                                                }}
                                                                name={icon}
                                                            />
                                                        </div>
                                                        <div
                                                            className={`${styles.text_wrapper} ${item.is_read || readNotifications.includes(item.id) ? `${styles.is_read}` : ''}`}
                                                            onClick={() => {
                                                                setChosenNotification(item);
                                                                onReadNotification(item);
                                                            }}>
                                                            <Typography
                                                                className={styles.title}
                                                                fw={500}
                                                                variant="h5">
                                                                {item.title}
                                                            </Typography>
                                                            <Typography
                                                                className={styles.time}
                                                                variant="body3">
                                                                {date}
                                                            </Typography>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        )}{' '}
                        {isNotificationsLoading && (
                            <>
                                <Skeleton className={styles.skeleton} />
                                <Skeleton className={styles.skeleton} />
                                <Skeleton className={styles.skeleton} />
                                <Skeleton className={styles.skeleton} />
                                <Skeleton className={styles.skeleton} />
                                <Skeleton className={styles.skeleton} />
                                <Skeleton className={styles.skeleton} />
                            </>
                        )}
                        {totalNotifications !== 0 && totalNotifications > notifications?.length && (
                            <Button
                                className={styles.load_more_button}
                                onClick={loadMoreNotifications}
                                variant="text-gray"
                                rightIcon="nav_down">
                                {submitLocale('load_more')}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
export default NotificationsSidebar;
